import type { z } from "zod";
import {
  acceptInvitationRequestSchema,
  accountEmptyOkResponseSchema,
  accountFeedbackSubmitRequestSchema,
  accountFeedbackSubmitResponseSchema,
  accountPasswordChangeRequestSchema,
  accountPasswordChangeResponseSchema,
  accountProfileResponseSchema,
  accountProfileUpdateRequestSchema,
  accountSessionsResponseSchema,
  accountThemeUpdateRequestSchema,
  accountThemeUpdateResponseSchema,
  adminAuditListResponseSchema,
  adminBookingsListResponseSchema,
  adminDepartmentCreateRequestSchema,
  adminDepartmentResponseSchema,
  adminDepartmentUpdateRequestSchema,
  adminDepartmentsListResponseSchema,
  adminEmptyOkResponseSchema,
  adminFareOverrideSetRequestSchema,
  offsiteApprovalRequestSchema,
  adminInvitationsListResponseSchema,
  adminInviteUserRequestSchema,
  adminInviteUserResponseSchema,
  adminResendInvitationsBulkRequestSchema,
  adminResendInvitationsBulkResponseSchema,
  adminLocationCreateRequestSchema,
  adminLocationResponseSchema,
  adminLocationUpdateRequestSchema,
  adminLocationsListResponseSchema,
  adminMembersListResponseSchema,
  adminResyncTripRequestSchema,
  adminResyncTripResponseSchema,
  adminSyncTriplinkRequestSchema,
  adminSyncTriplinkResponseSchema,
  adminSyncTriplinkStatusResponseSchema,
  reportRecipientsResponseSchema,
  reportRecipientsUpdateRequestSchema,
  reportScheduleResponseSchema,
  reportScheduleUpdateRequestSchema,
  reportsOperationsDashboardResponseSchema,
  reportsSendDepartmentSummaryRequestSchema,
  reportsSendDepartmentSummaryResponseSchema,
  reportsSendMonthlyReportRequestSchema,
  reportsSendMonthlyReportResponseSchema,
  reportsSendOffsiteReviewRequestSchema,
  reportsSendOffsiteReviewResponseSchema,
  reportsSendWeeklyDigestRequestSchema,
  reportsSendWeeklyDigestResponseSchema,
  tmsReconciliationListResponseSchema,
  tmsReconciliationResolveRequestSchema,
  tmsReconciliationResolveResponseSchema,
  adminUnmatchedDepartmentResolveRequestSchema,
  adminUnmatchedDepartmentsListResponseSchema,
  adminUpdateUserRequestSchema,
  adminUpdateUserResponseSchema,
  adminUsersListResponseSchema,
  invoiceGenerateRequestSchema,
  invoiceGenerateResponseSchema,
  invoiceGetResponseSchema,
  invoiceMarkPaidRequestSchema,
  invoiceMutationResponseSchema,
  invoicePreviewRequestSchema,
  invoicePreviewResponseSchema,
  invoiceReviewResponseSchema,
  invoiceVoidRequestSchema,
  invoicesListResponseSchema,
  authSuccessSchema,
  loginRequestSchema,
  logoutResponseSchema,
  magicLinkConsumeRequestSchema,
  magicLinkRequestResponseSchema,
  magicLinkRequestSchema,
  meResponseSchema,
  metaDepartmentsResponseSchema,
  metaLocationsResponseSchema,
  passwordResetConsumeRequestSchema,
  passwordResetConsumeResponseSchema,
  passwordResetRequestResponseSchema,
  passwordResetRequestSchema,
  patientDeleteByMrnRequestSchema,
  patientDeleteByMrnResponseSchema,
  patientLookupRequestSchema,
  patientLookupResponseSchema,
  patientMergeRequestSchema,
  patientMergeResponseSchema,
  patientUpdateDobRequestSchema,
  patientUpdateDobResponseSchema,
  patientUpdateMrnRequestSchema,
  patientUpdateMrnResponseSchema,
  permissionsResponseSchema,
  permissionsUpdateRequestSchema,
  patientUpdateNameRequestSchema,
  patientUpdateNameResponseSchema,
  patientUpdatePhoneRequestSchema,
  patientUpdatePhoneResponseSchema,
  tripActivateWillCallRequestSchema,
  tripActivateWillCallResponseSchema,
  tripCancelRequestSchema,
  tripUpdateRequestSchema,
  tripUpdateResponseSchema,
  tripCancelResponseSchema,
  tripCreateRecurringRequestSchema,
  tripCreateRecurringResponseSchema,
  tripCreateRequestSchema,
  tripCreateResponseSchema,
  tripEtaRequestSchema,
  tripEtaResponseSchema,
  tripScheduleRequestSchema,
  tripScheduleResponseSchema,
} from "@mtform/shared";
import { z as zod } from "zod";
import { Sentry } from "./sentry";

// =============================================================================
// Typed API client. Every endpoint validates its response against the shared
// Zod schema so the frontend won't silently rot if the BFF changes shape.
// =============================================================================

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// A single hook the auth layer registers so it learns the session went invalid
// on ANY call — query, mutation, or background poll. Fired once per call on a
// 401 UNAUTHENTICATED (see request()), before the ApiError is thrown, so
// AuthContext can sign the user out + redirect to /login.
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

/** Should this API error be reported to Sentry? Only genuinely-unexpected
 *  faults: a server error (5xx) or contract drift (BAD_RESPONSE — the BFF
 *  returned a shape the client couldn't parse, usually after a deploy that
 *  changed the response). We deliberately stay silent on the normal
 *  4xx / auth / validation / rate-limit / cancellation conversation, which is
 *  the product working as designed and would drown the real signals. */
export function shouldReportApiError(error: ApiError): boolean {
  if (error.code === "BAD_RESPONSE") return true;
  // "Briefly unavailable" (the database couldn't be reached) is reported by
  // the server, once per request; every open tab reporting it too would
  // flood Sentry during an outage.
  if (error.code === "SERVICE_UNAVAILABLE") return false;
  return error.status >= 500 && error.status < 600;
}

/** Report an unexpected API error to Sentry (no-op when Sentry isn't live, or
 *  when shouldReportApiError says it's expected), then return it to be thrown.
 *  Tags are non-PHI metadata only, and it never throws — observability must
 *  never break the request path. This is the single web choke point, so it
 *  covers both React Query calls and direct calls (e.g. login). */
function reportApiError(error: ApiError): ApiError {
  if (shouldReportApiError(error)) {
    try {
      Sentry.captureException(error, {
        tags: {
          api_code: error.code,
          api_status: String(error.status),
          ...(error.code === "BAD_RESPONSE" ? { contract_drift: "true" } : {}),
        },
      });
    } catch {
      /* swallow — Sentry must never break the request path */
    }
  }
  return error;
}

const errorEnvelope = zod.object({
  ok: zod.literal(false),
  error: zod.object({
    code: zod.string(),
    message: zod.string(),
    details: zod.unknown().optional(),
  }),
});

const successEnvelope = zod.object({
  ok: zod.literal(true),
  response: zod.unknown(),
});

/** Default ceiling on an API call. `fetch` has no built-in timeout, so a hung
 *  or half-open connection would otherwise leave the UI spinning forever. 45s
 *  is far above any normal request; genuinely long-running calls override it
 *  per-request via the `timeoutMs` arg, so this never cuts off slow-but-valid
 *  work. */
const REQUEST_TIMEOUT_MS = 45_000;
/** TripLink backfill can fan out ~120 upstream schedule fetches in one click;
 *  the manual sync gets a much higher ceiling so it's never aborted mid-run. */
const SYNC_TIMEOUT_MS = 5 * 60_000;
/** "Send now" report actions dispatch email to every recipient — headroom over
 *  the 45s default. */
const REPORT_SEND_TIMEOUT_MS = 2 * 60_000;
/** A repeating booking books its dates one after another, one dispatch booking
 *  each (live single bookings: p99 1.9s, slowest 3.1s), so its ceiling grows
 *  with the series, up to just under Cloud Run's 300s limit on any request.
 *  Giving up while the server is still booking leaves the booker not knowing
 *  which dates booked. */
const RECURRING_TIMEOUT_PER_DATE_MS = 5_000;
const RECURRING_TIMEOUT_MAX_MS = 290_000;

async function request<T extends z.ZodTypeAny>(
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  path: string,
  schema: T,
  body?: unknown,
  signal?: AbortSignal,
  timeoutMs: number = REQUEST_TIMEOUT_MS,
): Promise<z.infer<T>> {
  // Bridge the caller's signal (React Query cancellation) and our own timeout
  // into one controller — fetch accepts only a single signal.
  const controller = new AbortController();
  let timedOut = false;
  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  const forwardAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener("abort", forwardAbort, { once: true });
  }

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers: body ? { "Content-Type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
      // Send and accept the session cookie on every API call, both in
      // dev (Vite proxy) and prod (same origin, redundant but explicit).
      credentials: "include",
    });
  } catch {
    if (timedOut) {
      throw new ApiError(
        "TIMEOUT",
        "The server took too long to respond. Please try again.",
        0,
      );
    }
    throw new ApiError("NETWORK_ERROR", "Network unreachable", 0);
  } finally {
    clearTimeout(timeout);
    if (signal) signal.removeEventListener("abort", forwardAbort);
  }

  let json: unknown;
  try {
    json = await response.json();
  } catch {
    throw reportApiError(
      new ApiError("BAD_RESPONSE", "Server returned a non-JSON response", response.status),
    );
  }

  // First check whether this is an error envelope.
  const errorParsed = errorEnvelope.safeParse(json);
  if (errorParsed.success) {
    // A 401 with the auth middleware's UNAUTHENTICATED code means the session
    // is gone (expired or revoked) — distinct from INVALID_CREDENTIALS (a bad
    // login) or FORBIDDEN (a permission gate), which must NOT sign the user
    // out. Notify the auth layer before the caller sees the error.
    if (
      response.status === 401 &&
      errorParsed.data.error.code === "UNAUTHENTICATED"
    ) {
      onUnauthorized?.();
    }
    throw reportApiError(
      new ApiError(
        errorParsed.data.error.code,
        errorParsed.data.error.message,
        response.status,
        errorParsed.data.error.details,
      ),
    );
  }

  // Otherwise it should be a success envelope wrapping the typed response.
  const okParsed = successEnvelope.safeParse(json);
  if (!okParsed.success) {
    console.error("[api] envelope did not match either shape:", okParsed.error);
    throw reportApiError(
      new ApiError("BAD_RESPONSE", "Unexpected response shape", response.status),
    );
  }

  const inner = schema.safeParse(okParsed.data.response);
  if (!inner.success) {
    console.error("[api] response inner did not match schema:", inner.error);
    throw reportApiError(
      new ApiError("BAD_RESPONSE", "Unexpected response shape", response.status),
    );
  }

  return inner.data;
}

// ---- Endpoints --------------------------------------------------------------

export const configSchema = zod.object({
  mapsApiKey: zod.string(),
  version: zod.string(),
});
export type AppConfig = z.infer<typeof configSchema>;

export const api = {
  meta: {
    config: (signal?: AbortSignal) => request("GET", "/api/meta/config", configSchema, undefined, signal),
    version: (signal?: AbortSignal) =>
      request("GET", "/api/meta/version", zod.object({ version: zod.string() }), undefined, signal),
    departments: (signal?: AbortSignal) =>
      request("GET", "/api/meta/departments", metaDepartmentsResponseSchema, undefined, signal),
    locations: (signal?: AbortSignal) =>
      request("GET", "/api/meta/locations", metaLocationsResponseSchema, undefined, signal),
  },
  auth: {
    me: (signal?: AbortSignal) =>
      request("GET", "/api/auth/me", meResponseSchema, undefined, signal),
    login: (req: z.input<typeof loginRequestSchema>, signal?: AbortSignal) =>
      request("POST", "/api/auth/login", authSuccessSchema, req, signal),
    requestMagicLink: (
      req: z.input<typeof magicLinkRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/auth/magic-link/request",
        magicLinkRequestResponseSchema,
        req,
        signal,
      ),
    consumeMagicLink: (
      req: z.input<typeof magicLinkConsumeRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/auth/magic-link/consume",
        authSuccessSchema,
        req,
        signal,
      ),
    acceptInvitation: (
      req: z.input<typeof acceptInvitationRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/auth/invitation/accept",
        authSuccessSchema,
        req,
        signal,
      ),
    logout: (signal?: AbortSignal) =>
      request("POST", "/api/auth/logout", logoutResponseSchema, undefined, signal),
    requestPasswordReset: (
      req: z.input<typeof passwordResetRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/auth/password-reset/request",
        passwordResetRequestResponseSchema,
        req,
        signal,
      ),
    consumePasswordReset: (
      req: z.input<typeof passwordResetConsumeRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/auth/password-reset/consume",
        passwordResetConsumeResponseSchema,
        req,
        signal,
      ),
  },
  account: {
    me: (signal?: AbortSignal) =>
      request("GET", "/api/account", accountProfileResponseSchema, undefined, signal),
    updateProfile: (
      req: z.input<typeof accountProfileUpdateRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "PATCH",
        "/api/account/profile",
        accountProfileResponseSchema,
        req,
        signal,
      ),
    changePassword: (
      req: z.input<typeof accountPasswordChangeRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/account/password",
        accountPasswordChangeResponseSchema,
        req,
        signal,
      ),
    /** Persist the user's UI theme choice to their account row.
     *  Fires from the ThemeProvider on every successful pick; the
     *  return value is the canonical theme we just stored, used to
     *  reconcile localStorage with the server's view. */
    updateTheme: (
      req: z.input<typeof accountThemeUpdateRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/account/theme",
        accountThemeUpdateResponseSchema,
        req,
        signal,
      ),
    sendFeedback: (
      req: z.input<typeof accountFeedbackSubmitRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/account/feedback",
        accountFeedbackSubmitResponseSchema,
        req,
        signal,
      ),
    listSessions: (signal?: AbortSignal) =>
      request(
        "GET",
        "/api/account/sessions",
        accountSessionsResponseSchema,
        undefined,
        signal,
      ),
    revokeOtherSessions: (signal?: AbortSignal) =>
      request(
        "POST",
        "/api/account/sessions/revoke-others",
        accountEmptyOkResponseSchema,
        undefined,
        signal,
      ),
    revokeSession: (sessionId: string, signal?: AbortSignal) =>
      request(
        "DELETE",
        `/api/account/sessions/${encodeURIComponent(sessionId)}`,
        accountEmptyOkResponseSchema,
        undefined,
        signal,
      ),
  },
  patient: {
    lookup: (req: z.input<typeof patientLookupRequestSchema>, signal?: AbortSignal) =>
      request("POST", "/api/patient/lookup", patientLookupResponseSchema, req, signal),
    updateName: (
      req: z.input<typeof patientUpdateNameRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/patient/update-name",
        patientUpdateNameResponseSchema,
        patientUpdateNameRequestSchema.parse(req),
        signal,
      ),
    updateMrn: (
      req: z.input<typeof patientUpdateMrnRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/patient/update-mrn",
        patientUpdateMrnResponseSchema,
        patientUpdateMrnRequestSchema.parse(req),
        signal,
      ),
    updateDob: (
      req: z.input<typeof patientUpdateDobRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/patient/update-dob",
        patientUpdateDobResponseSchema,
        patientUpdateDobRequestSchema.parse(req),
        signal,
      ),
    updatePhone: (
      req: z.input<typeof patientUpdatePhoneRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/patient/update-phone",
        patientUpdatePhoneResponseSchema,
        patientUpdatePhoneRequestSchema.parse(req),
        signal,
      ),
    merge: (
      req: z.input<typeof patientMergeRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/patient/merge",
        patientMergeResponseSchema,
        patientMergeRequestSchema.parse(req),
        signal,
      ),
    deleteByMrn: (
      req: z.input<typeof patientDeleteByMrnRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/patient/delete-by-mrn",
        patientDeleteByMrnResponseSchema,
        patientDeleteByMrnRequestSchema.parse(req),
        signal,
      ),
  },
  trip: {
    create: (req: z.input<typeof tripCreateRequestSchema>, signal?: AbortSignal) =>
      request("POST", "/api/trip/create", tripCreateResponseSchema, req, signal),
    createRecurring: (
      req: z.input<typeof tripCreateRecurringRequestSchema>,
      signal?: AbortSignal,
    ) => {
      const waitMs = Math.min(
        REQUEST_TIMEOUT_MS + req.dates.length * RECURRING_TIMEOUT_PER_DATE_MS,
        RECURRING_TIMEOUT_MAX_MS,
      );
      // Told to the server, which stops starting dates shortly before then,
      // so its answer arrives while the form is still waiting.
      return request(
        "POST",
        "/api/trip/create-recurring",
        tripCreateRecurringResponseSchema,
        { ...req, waitMs },
        signal,
        waitMs,
      );
    },
    eta: (req: z.input<typeof tripEtaRequestSchema>, signal?: AbortSignal) =>
      request("POST", "/api/trip/eta", tripEtaResponseSchema, req, signal),
    schedule: (req: z.input<typeof tripScheduleRequestSchema>, signal?: AbortSignal) =>
      request("POST", "/api/trip/schedule", tripScheduleResponseSchema, req, signal),
    activateWillCall: (
      req: z.input<typeof tripActivateWillCallRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/trip/activate-will-call",
        tripActivateWillCallResponseSchema,
        req,
        signal,
      ),
    cancel: (
      req: z.input<typeof tripCancelRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/trip/cancel",
        tripCancelResponseSchema,
        req,
        signal,
      ),
    update: (
      req: z.input<typeof tripUpdateRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/trip/update",
        tripUpdateResponseSchema,
        req,
        signal,
      ),
  },
  admin: {
    getPermissions: (signal?: AbortSignal) =>
      request(
        "GET",
        "/api/admin/permissions",
        permissionsResponseSchema,
        undefined,
        signal,
      ),
    updatePermissions: (
      req: z.input<typeof permissionsUpdateRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "PUT",
        "/api/admin/permissions",
        permissionsResponseSchema,
        permissionsUpdateRequestSchema.parse(req),
        signal,
      ),
    listUsers: (
      params: {
        search?: string;
        role?: string;
        status?: string;
        limit?: number;
        beforeCreatedAt?: string;
        beforeId?: string;
      },
      signal?: AbortSignal,
    ) => {
      const qs = buildQueryString(params);
      const path = qs ? `/api/admin/users?${qs}` : "/api/admin/users";
      return request("GET", path, adminUsersListResponseSchema, undefined, signal);
    },
    listInvitations: (
      params: {
        search?: string;
        role?: string;
        limit?: number;
        beforeCreatedAt?: string;
        beforeId?: string;
      },
      signal?: AbortSignal,
    ) => {
      const qs = buildQueryString(params);
      const path = qs ? `/api/admin/invitations?${qs}` : "/api/admin/invitations";
      return request(
        "GET",
        path,
        adminInvitationsListResponseSchema,
        undefined,
        signal,
      );
    },
    inviteUser: (
      req: z.input<typeof adminInviteUserRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/admin/users/invite",
        adminInviteUserResponseSchema,
        req,
        signal,
      ),
    updateUser: (
      userId: string,
      req: z.input<typeof adminUpdateUserRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "PATCH",
        `/api/admin/users/${encodeURIComponent(userId)}`,
        adminUpdateUserResponseSchema,
        req,
        signal,
      ),
    revokeUserSessions: (userId: string, signal?: AbortSignal) =>
      request(
        "POST",
        `/api/admin/users/${encodeURIComponent(userId)}/revoke-sessions`,
        adminEmptyOkResponseSchema,
        undefined,
        signal,
      ),
    sendPasswordReset: (userId: string, signal?: AbortSignal) =>
      request(
        "POST",
        `/api/admin/users/${encodeURIComponent(userId)}/send-password-reset`,
        passwordResetRequestResponseSchema,
        undefined,
        signal,
      ),
    resendInvitationsBulk: (
      req: z.input<typeof adminResendInvitationsBulkRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/admin/invitations/resend-bulk",
        adminResendInvitationsBulkResponseSchema,
        req,
        signal,
      ),
    revokeInvitation: (invitationId: string, signal?: AbortSignal) =>
      request(
        "DELETE",
        `/api/admin/invitations/${encodeURIComponent(invitationId)}`,
        adminEmptyOkResponseSchema,
        undefined,
        signal,
      ),
    listAudit: (
      params: {
        action?: string;
        userId?: string;
        outcome?: string;
        since?: string;
        until?: string;
        includeSystem?: boolean;
        limit?: number;
        beforeId?: string;
      },
      signal?: AbortSignal,
    ) => {
      const qs = buildQueryString(params);
      const path = qs ? `/api/admin/audit?${qs}` : "/api/admin/audit";
      return request("GET", path, adminAuditListResponseSchema, undefined, signal);
    },
    listMembers: (
      params: {
        search?: string;
        origin?: "tms" | "form";
        needsCleanup?: boolean;
        limit?: number;
        beforeLastTrip?: string;
        beforeMrn?: string;
      },
      signal?: AbortSignal,
    ) => {
      const qs = buildQueryString(params);
      const path = qs ? `/api/admin/members?${qs}` : "/api/admin/members";
      return request(
        "GET",
        path,
        adminMembersListResponseSchema,
        undefined,
        signal,
      );
    },
    syncTriplinkStatus: (signal?: AbortSignal) =>
      request(
        "GET",
        "/api/admin/sync-triplink/status",
        adminSyncTriplinkStatusResponseSchema,
        undefined,
        signal,
      ),
    syncTriplink: (
      req: z.input<typeof adminSyncTriplinkRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/admin/sync-triplink",
        adminSyncTriplinkResponseSchema,
        adminSyncTriplinkRequestSchema.parse(req),
        signal,
        SYNC_TIMEOUT_MS,
      ),
    resyncTrip: (
      req: z.input<typeof adminResyncTripRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/admin/sync-triplink/trip",
        adminResyncTripResponseSchema,
        adminResyncTripRequestSchema.parse(req),
        signal,
      ),
    listBookings: (
      params: {
        search?: string;
        mrn?: string;
        dateFrom?: string;
        dateTo?: string;
        department?: string;
        tripType?: string;
        transportType?: string;
        status?: string;
        hideCancelled?: boolean;
        origin?: "tms" | "form";
        missingMrn?: boolean;
        noFare?: boolean;
        offSite?: boolean;
        offSiteView?: "review" | "approved";
        createdByUserId?: string;
        limit?: number;
        beforeCreatedAt?: string;
        beforeId?: string;
      },
      signal?: AbortSignal,
    ) => {
      const qs = buildQueryString(params);
      const path = qs ? `/api/admin/bookings?${qs}` : "/api/admin/bookings";
      return request(
        "GET",
        path,
        adminBookingsListResponseSchema,
        undefined,
        signal,
      );
    },
    deleteBooking: (id: string, signal?: AbortSignal) =>
      request(
        "DELETE",
        `/api/admin/bookings/${encodeURIComponent(id)}`,
        adminEmptyOkResponseSchema,
        undefined,
        signal,
      ),
    /** Hand-set one leg's fare (Royal super-admins only). The amount
     *  replaces the computed fare; the IT fee is still added on top. */
    setFareOverride: (
      id: string,
      req: z.input<typeof adminFareOverrideSetRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "PUT",
        `/api/admin/bookings/${encodeURIComponent(id)}/fare-override`,
        adminEmptyOkResponseSchema,
        adminFareOverrideSetRequestSchema.parse(req),
        signal,
      ),
    clearFareOverride: (id: string, signal?: AbortSignal) =>
      request(
        "DELETE",
        `/api/admin/bookings/${encodeURIComponent(id)}/fare-override`,
        adminEmptyOkResponseSchema,
        undefined,
        signal,
      ),
    /** Approve an off-site trip (every leg of it), with an optional note.
     *  `written`: the booking ids approved by this call (legs already
     *  approved as they are keep their approval). */
    approveOffsiteTrip: (id: string, note: string, signal?: AbortSignal) =>
      request(
        "POST",
        `/api/admin/bookings/${encodeURIComponent(id)}/offsite-approval`,
        zod.object({ approved: zod.array(zod.string()), written: zod.array(zod.string()) }),
        offsiteApprovalRequestSchema.parse({ note }),
        signal,
      ),
    /** Take an off-site trip's approval back: every leg of it, or only
     *  `legs` (booking ids), e.g. the ones an approve just wrote. */
    unapproveOffsiteTrip: (id: string, legs?: readonly string[], signal?: AbortSignal) =>
      request(
        "DELETE",
        `/api/admin/bookings/${encodeURIComponent(id)}/offsite-approval${
          legs ? `?legs=${encodeURIComponent(legs.join(","))}` : ""
        }`,
        zod.object({ removed: zod.array(zod.string()) }),
        undefined,
        signal,
      ),
    listDepartments: (signal?: AbortSignal) =>
      request(
        "GET",
        "/api/admin/departments",
        adminDepartmentsListResponseSchema,
        undefined,
        signal,
      ),
    listUnmatchedDepartments: (signal?: AbortSignal) =>
      request(
        "GET",
        "/api/admin/departments/unmatched",
        adminUnmatchedDepartmentsListResponseSchema,
        undefined,
        signal,
      ),
    resolveUnmatchedDepartment: (
      req: z.input<typeof adminUnmatchedDepartmentResolveRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/admin/departments/unmatched/resolve",
        adminEmptyOkResponseSchema,
        req,
        signal,
      ),
    createDepartment: (
      req: z.input<typeof adminDepartmentCreateRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/admin/departments",
        adminDepartmentResponseSchema,
        req,
        signal,
      ),
    updateDepartment: (
      id: string,
      req: z.input<typeof adminDepartmentUpdateRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "PATCH",
        `/api/admin/departments/${encodeURIComponent(id)}`,
        adminDepartmentResponseSchema,
        req,
        signal,
      ),
    deactivateDepartment: (id: string, signal?: AbortSignal) =>
      request(
        "POST",
        `/api/admin/departments/${encodeURIComponent(id)}/deactivate`,
        adminEmptyOkResponseSchema,
        undefined,
        signal,
      ),
    deleteDepartment: (id: string, signal?: AbortSignal) =>
      request(
        "DELETE",
        `/api/admin/departments/${encodeURIComponent(id)}`,
        adminEmptyOkResponseSchema,
        undefined,
        signal,
      ),
    listLocations: (signal?: AbortSignal) =>
      request(
        "GET",
        "/api/admin/locations",
        adminLocationsListResponseSchema,
        undefined,
        signal,
      ),
    createLocation: (
      req: z.input<typeof adminLocationCreateRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/admin/locations",
        adminLocationResponseSchema,
        req,
        signal,
      ),
    updateLocation: (
      id: string,
      req: z.input<typeof adminLocationUpdateRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "PATCH",
        `/api/admin/locations/${encodeURIComponent(id)}`,
        adminLocationResponseSchema,
        req,
        signal,
      ),
    deactivateLocation: (id: string, signal?: AbortSignal) =>
      request(
        "POST",
        `/api/admin/locations/${encodeURIComponent(id)}/deactivate`,
        adminEmptyOkResponseSchema,
        undefined,
        signal,
      ),
    deleteLocation: (id: string, signal?: AbortSignal) =>
      request(
        "DELETE",
        `/api/admin/locations/${encodeURIComponent(id)}`,
        adminEmptyOkResponseSchema,
        undefined,
        signal,
      ),
    getOperationsDashboard: (
      params?: { dateFrom?: string; dateTo?: string },
      signal?: AbortSignal,
    ) => {
      const qs = new URLSearchParams();
      if (params?.dateFrom) qs.set("dateFrom", params.dateFrom);
      if (params?.dateTo) qs.set("dateTo", params.dateTo);
      const suffix = qs.toString() ? `?${qs.toString()}` : "";
      return request(
        "GET",
        `/api/admin/reports/operations-dashboard${suffix}`,
        reportsOperationsDashboardResponseSchema,
        undefined,
        signal,
      );
    },
    // ---- Invoices (Royal billing) ----
    previewInvoice: (
      req: z.input<typeof invoicePreviewRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/admin/invoices/preview",
        invoicePreviewResponseSchema,
        req,
        signal,
      ),
    generateInvoice: (
      req: z.input<typeof invoiceGenerateRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/admin/invoices/generate",
        invoiceGenerateResponseSchema,
        req,
        signal,
      ),
    listInvoices: (
      params?: {
        status?: string;
        search?: string;
        limit?: number;
        beforeCreatedAt?: string;
        beforeId?: string;
      },
      signal?: AbortSignal,
    ) => {
      const qs = new URLSearchParams();
      if (params?.status) qs.set("status", params.status);
      if (params?.search) qs.set("search", params.search);
      if (params?.limit) qs.set("limit", String(params.limit));
      if (params?.beforeCreatedAt) qs.set("beforeCreatedAt", params.beforeCreatedAt);
      if (params?.beforeId) qs.set("beforeId", params.beforeId);
      const suffix = qs.toString() ? `?${qs.toString()}` : "";
      return request(
        "GET",
        `/api/admin/invoices${suffix}`,
        invoicesListResponseSchema,
        undefined,
        signal,
      );
    },
    getInvoice: (id: string, signal?: AbortSignal) =>
      request(
        "GET",
        `/api/admin/invoices/${encodeURIComponent(id)}`,
        invoiceGetResponseSchema,
        undefined,
        signal,
      ),
    // READ-ONLY invoice-review report for a window (no mutation companion).
    getInvoiceReview: (
      params: { dateFrom: string; dateTo: string },
      signal?: AbortSignal,
    ) => {
      const qs = new URLSearchParams();
      qs.set("dateFrom", params.dateFrom);
      qs.set("dateTo", params.dateTo);
      return request(
        "GET",
        `/api/admin/invoices/review?${qs.toString()}`,
        invoiceReviewResponseSchema,
        undefined,
        signal,
      );
    },
    markInvoiceSent: (id: string, signal?: AbortSignal) =>
      request(
        "POST",
        `/api/admin/invoices/${encodeURIComponent(id)}/mark-sent`,
        invoiceMutationResponseSchema,
        undefined,
        signal,
      ),
    markInvoicePaid: (
      id: string,
      req: z.input<typeof invoiceMarkPaidRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        `/api/admin/invoices/${encodeURIComponent(id)}/mark-paid`,
        invoiceMutationResponseSchema,
        req,
        signal,
      ),
    voidInvoice: (
      id: string,
      req: z.input<typeof invoiceVoidRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        `/api/admin/invoices/${encodeURIComponent(id)}/void`,
        invoiceMutationResponseSchema,
        req,
        signal,
      ),
    regenerateInvoice: (id: string, signal?: AbortSignal) =>
      request(
        "POST",
        `/api/admin/invoices/${encodeURIComponent(id)}/regenerate`,
        invoiceMutationResponseSchema,
        undefined,
        signal,
      ),
    getReportRecipients: (signal?: AbortSignal) =>
      request(
        "GET",
        "/api/admin/reports/recipients",
        reportRecipientsResponseSchema,
        undefined,
        signal,
      ),
    updateReportRecipients: (
      req: z.input<typeof reportRecipientsUpdateRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "PUT",
        "/api/admin/reports/recipients",
        reportRecipientsResponseSchema,
        reportRecipientsUpdateRequestSchema.parse(req),
        signal,
      ),
    getReportSchedule: (signal?: AbortSignal) =>
      request(
        "GET",
        "/api/admin/reports/schedule",
        reportScheduleResponseSchema,
        undefined,
        signal,
      ),
    updateReportSchedule: (
      req: z.input<typeof reportScheduleUpdateRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "PUT",
        "/api/admin/reports/schedule",
        reportScheduleResponseSchema,
        reportScheduleUpdateRequestSchema.parse(req),
        signal,
      ),
    sendMonthlyReportNow: (
      req: z.input<typeof reportsSendMonthlyReportRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/admin/reports/send-monthly-report-now",
        reportsSendMonthlyReportResponseSchema,
        reportsSendMonthlyReportRequestSchema.parse(req),
        signal,
        REPORT_SEND_TIMEOUT_MS,
      ),
    sendOffsiteReviewNow: (
      req: z.input<typeof reportsSendOffsiteReviewRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/admin/reports/send-offsite-review-now",
        reportsSendOffsiteReviewResponseSchema,
        reportsSendOffsiteReviewRequestSchema.parse(req),
        signal,
        REPORT_SEND_TIMEOUT_MS,
      ),
    sendWeeklyDigestNow: (
      req: z.input<typeof reportsSendWeeklyDigestRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/admin/reports/send-weekly-digest-now",
        reportsSendWeeklyDigestResponseSchema,
        reportsSendWeeklyDigestRequestSchema.parse(req),
        signal,
        REPORT_SEND_TIMEOUT_MS,
      ),
    sendDepartmentSummaryNow: (
      req: z.input<typeof reportsSendDepartmentSummaryRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        "/api/admin/reports/send-department-summary-now",
        reportsSendDepartmentSummaryResponseSchema,
        reportsSendDepartmentSummaryRequestSchema.parse(req),
        signal,
        REPORT_SEND_TIMEOUT_MS,
      ),
    listTmsReconciliation: (
      params: {
        status?: "pending" | "resolved" | "dismissed";
        mrn?: string;
        limit?: number;
        beforeCreatedAt?: string;
        beforeId?: string;
      },
      signal?: AbortSignal,
    ) => {
      const qs = buildQueryString(params);
      const path = qs
        ? `/api/admin/tms-reconciliation?${qs}`
        : "/api/admin/tms-reconciliation";
      return request(
        "GET",
        path,
        tmsReconciliationListResponseSchema,
        undefined,
        signal,
      );
    },
    resolveTmsReconciliation: (
      id: string,
      req: z.input<typeof tmsReconciliationResolveRequestSchema>,
      signal?: AbortSignal,
    ) =>
      request(
        "POST",
        `/api/admin/tms-reconciliation/${encodeURIComponent(id)}/resolve`,
        tmsReconciliationResolveResponseSchema,
        tmsReconciliationResolveRequestSchema.parse(req),
        signal,
      ),
  },
};

function buildQueryString(
  params: Record<string, string | number | boolean | undefined>,
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "" || value === false) continue;
    search.set(key, String(value));
  }
  return search.toString();
}
